const Settings = {
    videoEncoderCodecs: [
        'hvc1.1.6.L93.90', // H.265/HEVC codec (widely supported)
        'avc1.4d0033', // H.264/AVC codec (widely supported)
        'avc1.420034',
    ],
    //videoEncoderCodec: 'avc1.420034',
    videoEncoderBitrateSd: 250_000, // broadcaster upload bw requirement (4x) 1Mbit/s
    videoEncoderDefaultFrameRate: 30, // Changed to 30fps
    videoEncoderDefaultWidth: 1920, // Full HD width
    videoEncoderDefaultHeight: 1080};

/**
 * Attempts to detect a supported `VideoEncoderConfig` by testing a set of codecs and parameters.
 *
 * The function iterates over the provided codec list (or defaults from `Settings`), adjusting
 * video width and height to be even numbers (required by some encoders). For each codec, it
 * calls `VideoEncoder.isConfigSupported` to determine if the configuration is supported in
 * the current browser environment.
 *
 * @param options - Partial configuration object to override default encoder settings.
 * @returns A Promise resolving to a valid `VideoEncoderConfig` if one is found, otherwise `null`.
 *
 * @example
 * ```ts
 * const config = await detectVideoEncoderConfig({
 *   bitrate: 2_000_000,
 *   codecs: ['vp9', 'avc1.42E01E'],
 *   frameRate: 30
 * });
 *
 * if (config) {
 *   console.log("Using config:", config);
 * } else {
 *   console.error("No supported config found.");
 * }
 * ```
 */
async function detectVideoEncoderConfig(options = {}) {
    const { bitrate = Settings.videoEncoderBitrateSd, codecs = Settings.videoEncoderCodecs, frameRate = Settings.videoEncoderDefaultFrameRate, hardwareAcceleration = 'prefer-hardware', height: heightParam = Settings.videoEncoderDefaultHeight, latencyMode = 'quality', width: widthParam = Settings.videoEncoderDefaultWidth, } = options;
    // Ensure width and height are even numbers (required by many encoders)
    const width = widthParam - (widthParam % 2);
    const height = heightParam - (heightParam % 2);
    for (const codec of codecs) {
        const config = {
            bitrate,
            codec,
            framerate: frameRate,
            hardwareAcceleration,
            height,
            latencyMode,
            width,
        };
        try {
            const codecSupport = await VideoEncoder.isConfigSupported(config);
            if (codecSupport.supported) {
                console.log('Found supported codec:', codec);
                return config;
            }
            console.log('This browser does not support codec:', codec);
        }
        catch (error) {
            console.warn(`Error checking support for codec ${codec} with config:`, config, error);
        }
    }
    console.error('Could not find supported codec configuration. Tried:', codecs.join(', '));
    return null;
}

export { detectVideoEncoderConfig };
